import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Building2,
  Store,
  MapPin,
  Phone,
  Plus,
  Trash2,
  Check,
  ChevronDown,
  Navigation,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Users,
  Globe,
} from "lucide-react";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
} from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import Config from "../../Config";
import { regionOptions, locationsByRegion } from "../../utils/data";

const InlinePartnerModal = ({ isOpen, onClose, onPartnerAdded }) => {
  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Partner data state
  const [partnerData, setPartnerData] = useState({
    name: "",
    branches: [],
  });

  // Current branch being edited
  const [currentBranch, setCurrentBranch] = useState({
    id: null,
    name: "",
    region: "",
    location: "",
    customLocation: "",
    district: "",
    suburb: "",
    landmark: "",
    gpsAddress: "",
    phoneNumber: "",
  });

  // Form states
  const [errors, setErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Animation states
  const [slideDirection, setSlideDirection] = useState(1);

  const steps = [
    {
      id: 1,
      title: "Partner Info",
      subtitle: "Basic details",
      icon: Building2,
    },
    {
      id: 2,
      title: "Add Branches",
      subtitle: "Location details",
      icon: Store,
    },
    {
      id: 3,
      title: "Review",
      subtitle: "Confirm & save",
      icon: Check,
    },
  ];

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setPartnerData({ name: "", branches: [] });
      setCurrentBranch({
        id: null,
        name: "",
        region: "",
        location: "",
        customLocation: "",
        district: "",
        suburb: "",
        landmark: "",
        gpsAddress: "",
        phoneNumber: "",
      });
      setErrors({});
      setIsEditing(false);
      setIsCustomLocation(false);
      setSearchTerm("");
    }
  }, [isOpen]);

  // Update available locations when region changes
  useEffect(() => {
    if (currentBranch.region) {
      const locations = locationsByRegion[currentBranch.region] || [];
      setAvailableLocations([...locations, "Other"]);
    }
  }, [currentBranch.region]);

  // Validation functions
  const validatePartnerName = (name) => {
    if (!name.trim()) return "Partner name is required";
    if (name.length < 3) return "Partner name must be at least 3 characters";
    if (name.length > 50) return "Partner name must be less than 50 characters";
    return "";
  };

  const validateBranchName = (name) => {
    if (!name.trim()) return "Branch name is required";
    if (name.length < 3) return "Branch name must be at least 3 characters";
    if (name.length > 50) return "Branch name must be less than 50 characters";
    return "";
  };

  const validatePhoneNumber = (number) => {
    if (!number) return "Phone number is required";
    if (number.length !== 9) return "Phone number must be exactly 9 digits";
    if (!/^\d+$/.test(number)) return "Phone number must contain only digits";
    return "";
  };

  const validateRegion = (region) => {
    if (!region) return "Region is required";
    return "";
  };

  const validateLocation = (location, customLocation) => {
    if (!location) return "Location is required";
    if (location === "Other" && !customLocation?.trim()) {
      return "Custom location is required";
    }
    return "";
  };

  // Navigation functions
  const nextStep = () => {
    if (currentStep === 1) {
      const partnerNameError = validatePartnerName(partnerData.name);
      if (partnerNameError) {
        setErrors({ partnerName: partnerNameError });
        toast.error(partnerNameError);
        return;
      }
    } else if (currentStep === 2) {
      if (partnerData.branches.length === 0) {
        toast.error("Please add at least one branch");
        return;
      }
    }
    
    setSlideDirection(1);
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    setSlideDirection(-1);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Branch management functions
  const addBranch = () => {
    const branchErrors = {};
    
    // Validate branch fields
    const nameError = validateBranchName(currentBranch.name);
    const phoneError = validatePhoneNumber(currentBranch.phoneNumber);
    const regionError = validateRegion(currentBranch.region);
    const locationError = validateLocation(currentBranch.location, currentBranch.customLocation);
    
    if (nameError) branchErrors.branchName = nameError;
    if (phoneError) branchErrors.phoneNumber = phoneError;
    if (regionError) branchErrors.region = regionError;
    if (locationError) branchErrors.location = locationError;
    
    if (Object.keys(branchErrors).length > 0) {
      setErrors(branchErrors);
      const firstError = Object.values(branchErrors)[0];
      toast.error(firstError);
      return;
    }

    // Check for duplicate branch names
    const isDuplicate = partnerData.branches.some(
      (branch, index) => 
        branch.name.toLowerCase() === currentBranch.name.toLowerCase() && 
        (!isEditing || index !== currentBranch.id)
    );
    
    if (isDuplicate) {
      setErrors({ branchName: "Branch name already exists" });
      toast.error("Branch name already exists");
      return;
    }

    const newBranch = {
      ...currentBranch,
      location: currentBranch.location === "Other" ? currentBranch.customLocation : currentBranch.location,
    };

    if (isEditing) {
      // Update existing branch
      const updatedBranches = [...partnerData.branches];
      updatedBranches[currentBranch.id] = newBranch;
      setPartnerData({ ...partnerData, branches: updatedBranches });
      toast.success("Branch updated successfully!");
    } else {
      // Add new branch
      setPartnerData({
        ...partnerData,
        branches: [...partnerData.branches, newBranch],
      });
      toast.success("Branch added successfully!");
    }

    // Reset form
    setCurrentBranch({
      id: null,
      name: "",
      region: "",
      location: "",
      customLocation: "",
      district: "",
      suburb: "",
      landmark: "",
      gpsAddress: "",
      phoneNumber: "",
    });
    setIsEditing(false);
    setIsCustomLocation(false);
    setErrors({});
  };

  const editBranch = (index) => {
    const branch = partnerData.branches[index];
    setCurrentBranch({
      ...branch,
      id: index,
      customLocation: branch.location,
    });
    setIsEditing(true);
    setIsCustomLocation(availableLocations.includes(branch.location) ? false : true);
  };

  const deleteBranch = (index) => {
    const updatedBranches = partnerData.branches.filter((_, i) => i !== index);
    setPartnerData({ ...partnerData, branches: updatedBranches });
    toast.success("Branch deleted successfully!");
  };

  const cancelEdit = () => {
    setCurrentBranch({
      id: null,
      name: "",
      region: "",
      location: "",
      customLocation: "",
      district: "",
      suburb: "",
      landmark: "",
      gpsAddress: "",
      phoneNumber: "",
    });
    setIsEditing(false);
    setIsCustomLocation(false);
    setErrors({});
  };

  // Location handling
  const handleLocationSelect = (location) => {
    setCurrentBranch({
      ...currentBranch,
      location: location,
      customLocation: location === "Other" ? "" : location,
    });
    setIsCustomLocation(location === "Other");
    setLocationDropdownOpen(false);
    setSearchTerm("");
  };

  const getFilteredLocations = () => {
    if (!currentBranch.region) return [];
    const locations = locationsByRegion[currentBranch.region] || [];
    if (!searchTerm) return [...locations, "Other"];
    return locations.filter(location =>
      location.toLowerCase().includes(searchTerm.toLowerCase())
    ).concat(["Other"]);
  };

  // Submit function
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = {
        name: partnerData.name,
        branches: partnerData.branches.map(({ customLocation, id, ...branch }) => ({
          name: branch.name,
          region: branch.region,
          location: branch.location,
          district: branch.district || "",
          suburb: branch.suburb || "",
          landmark: branch.landmark || "",
          gpsAddress: branch.gpsAddress || "",
          phone_number: `233${branch.phoneNumber}`,
        })),
      };

      const response = await axios.post(
        `${Config.backendDomain}vendor/partner`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("dealboxx_token")}`,
          },
        }
      );

      const { data } = response;
      if (data?.data?.status_code === "001" && !data?.data?.in_error) {
        toast.success("Partner added successfully!");
        onPartnerAdded();
        onClose();
      } else {
        throw new Error(data?.data?.reason || "Failed to add partner");
      }
    } catch (error) {
      console.error("Error adding partner:", error);
      toast.error(error?.response?.data?.reason || error.message || "Failed to add partner");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed inset-0 z-50 justify-center items-center p-1 backdrop-blur-sm bg-black/60"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="relative w-full max-w-6xl max-h-[98vh] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header - Ultra Compressed */}
          <div className="relative p-3 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white shrink-0">
            <div className="flex justify-between items-center mb-2">
              <div className="flex gap-2 items-center">
                <div className="p-1.5 rounded-lg backdrop-blur-sm bg-white/20">
                  <Building2 className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Add New Partner</h2>
                  <p className="text-xs text-white/80">Create partner with branches</p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-1.5 rounded-full backdrop-blur-sm transition-colors bg-white/20 hover:bg-white/30"
              >
                <X className="w-4 h-4" />
              </motion.button>
            </div>

            {/* Progress Steps - Ultra Compressed */}
            <div className="flex justify-between items-center">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex gap-1.5 items-center">
                    <motion.div
                      className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ${
                        currentStep >= step.id
                          ? 'bg-white text-[#FF6C6C] shadow-lg'
                          : 'bg-white/20 text-white/60'
                      }`}
                      animate={{ scale: currentStep === step.id ? 1.05 : 1 }}
                    >
                      {currentStep > step.id ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        <step.icon className="w-3.5 h-3.5" />
                      )}
                    </motion.div>
                    <div className="hidden md:block">
                      <h3 className={`text-xs font-semibold ${
                        currentStep >= step.id ? 'text-white' : 'text-white/60'
                      }`}>
                        {step.title}
                      </h3>
                      <p className="text-xs text-white/70">{step.subtitle}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-1.5">
                      <div className={`h-0.5 rounded-full transition-colors duration-300 ${
                        currentStep > step.id ? 'bg-white' : 'bg-white/20'
                      }`} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="overflow-y-auto flex-1 p-3 min-h-0">
            <AnimatePresence mode="wait">
              {/* Step 1: Partner Information */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ x: slideDirection * 300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: slideDirection * -300, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="space-y-3"
                >
                  <div className="mb-4 text-center">
                    <div className="inline-flex justify-center items-center mb-2 w-10 h-10 text-white bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                      <Building2 className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Partner Information</h3>
                    <p className="text-xs text-gray-600">Enter the basic details for your new partner</p>
                  </div>

                  <div className="mx-auto max-w-sm">
                    <TextField
                      label="Partner Name"
                      value={partnerData.name}
                      onChange={(e) => {
                        setPartnerData({ ...partnerData, name: e.target.value });
                        if (errors.partnerName) {
                          setErrors({ ...errors, partnerName: "" });
                        }
                      }}
                      variant="outlined"
                      fullWidth
                      required
                      error={!!errors.partnerName}
                      helperText={errors.partnerName}
                      placeholder="e.g., ABC Electronics Ltd."
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          "&:hover fieldset": { borderColor: "#FF6C6C" },
                          "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                        },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                      }}
                    />
                  </div>

                  <div className="flex gap-1.5 justify-center items-center mt-4">
                    <Users className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-500">Step 1 of 3</span>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Add Branches */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ x: slideDirection * 300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: slideDirection * -300, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="space-y-3"
                >
                  <div className="mb-3 text-center">
                    <div className="inline-flex justify-center items-center mb-1.5 w-10 h-10 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
                      <Store className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Add Branches</h3>
                    <p className="text-xs text-gray-600">Add branches for {partnerData.name}</p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                    {/* Branch Form */}
                    <div className="space-y-2">
                      <div className="p-3 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-100">
                        <h4 className="flex gap-1.5 items-center mb-2 text-sm font-semibold text-gray-900">
                          <Store className="w-3.5 h-3.5 text-purple-600" />
                          {isEditing ? "Edit Branch" : "Add New Branch"}
                        </h4>
                        
                        <div className="space-y-2.5">
                          <TextField
                            label="Branch Name"
                            value={currentBranch.name}
                            onChange={(e) => {
                              setCurrentBranch({ ...currentBranch, name: e.target.value });
                              if (errors.branchName) {
                                setErrors({ ...errors, branchName: "" });
                              }
                            }}
                            variant="outlined"
                            fullWidth
                            required
                            error={!!errors.branchName}
                            helperText={errors.branchName}
                            placeholder="e.g., Main Branch"
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                "&:hover fieldset": { borderColor: "#FF6C6C" },
                                "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                              },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                            }}
                          />

                          <div className="grid grid-cols-2 gap-2">
                            <FormControl fullWidth size="small">
                              <InputLabel sx={{ "&.Mui-focused": { color: "#FF6C6C" } }}>
                                Region
                              </InputLabel>
                              <Select
                                value={currentBranch.region}
                                onChange={(e) => {
                                  setCurrentBranch({
                                    ...currentBranch,
                                    region: e.target.value,
                                    location: "",
                                    customLocation: "",
                                  });
                                  setIsCustomLocation(false);
                                  if (errors.region) {
                                    setErrors({ ...errors, region: "" });
                                  }
                                }}
                                label="Region"
                                error={!!errors.region}
                                sx={{
                                  borderRadius: "8px",
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#FF6C6C",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#FF6C6C",
                                  },
                                }}
                              >
                                {regionOptions.map((region) => (
                                  <MenuItem key={region} value={region}>
                                    {region}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            <div className="relative">
                              <TextField
                                label="Location"
                                value={isCustomLocation ? currentBranch.customLocation : currentBranch.location}
                                onChange={(e) => {
                                  if (isCustomLocation) {
                                    setCurrentBranch({ ...currentBranch, customLocation: e.target.value });
                                  } else {
                                    setSearchTerm(e.target.value);
                                    setLocationDropdownOpen(true);
                                  }
                                  if (errors.location) {
                                    setErrors({ ...errors, location: "" });
                                  }
                                }}
                                onFocus={() => {
                                  if (!isCustomLocation && currentBranch.region) {
                                    setLocationDropdownOpen(true);
                                  }
                                }}
                                variant="outlined"
                                fullWidth
                                required
                                error={!!errors.location}
                                helperText={errors.location}
                                placeholder={isCustomLocation ? "Enter custom location" : "Select location"}
                                disabled={!currentBranch.region}
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    "&:hover fieldset": { borderColor: "#FF6C6C" },
                                    "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                                  },
                                  "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                                }}
                              />

                              {/* Location Dropdown */}
                              <AnimatePresence>
                                {locationDropdownOpen && currentBranch.region && !isCustomLocation && (
                                  <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="overflow-y-auto absolute right-0 left-0 top-full z-50 mt-1 max-h-28 bg-white rounded-lg border border-gray-200 shadow-lg"
                                  >
                                    {getFilteredLocations().map((location) => (
                                      <div
                                        key={location}
                                        onClick={() => handleLocationSelect(location)}
                                        className="flex gap-1.5 items-center px-2.5 py-1.5 text-xs transition-colors cursor-pointer hover:bg-gray-50"
                                      >
                                        {location === "Other" ? (
                                          <Globe className="w-3 h-3 text-gray-400" />
                                        ) : (
                                          <MapPin className="w-3 h-3 text-[#FF6C6C]" />
                                        )}
                                        <span>{location}</span>
                                      </div>
                                    ))}
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>

                          <TextField
                            label="Phone Number"
                            value={currentBranch.phoneNumber}
                            onChange={(e) => {
                              let value = e.target.value.replace(/\D/g, "");
                              value = value.slice(0, 9);
                              setCurrentBranch({ ...currentBranch, phoneNumber: value });
                              if (errors.phoneNumber) {
                                setErrors({ ...errors, phoneNumber: "" });
                              }
                            }}
                            variant="outlined"
                            fullWidth
                            required
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber || "Format: 233XXXXXXXXX"}
                            placeholder="XXXXXXXXX"
                            size="small"
                            InputProps={{
                              startAdornment: <span className="mr-1.5 text-gray-500 text-sm">233</span>,
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                "&:hover fieldset": { borderColor: "#FF6C6C" },
                                "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                              },
                              "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                            }}
                          />

                          {/* Optional Fields - Collapsed */}
                          <div className="grid grid-cols-2 gap-2">
                            <TextField
                              label="District"
                              value={currentBranch.district}
                              onChange={(e) => setCurrentBranch({ ...currentBranch, district: e.target.value })}
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  "&:hover fieldset": { borderColor: "#FF6C6C" },
                                  "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                              }}
                            />

                            <TextField
                              label="Suburb"
                              value={currentBranch.suburb}
                              onChange={(e) => setCurrentBranch({ ...currentBranch, suburb: e.target.value })}
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  "&:hover fieldset": { borderColor: "#FF6C6C" },
                                  "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-1 gap-2">
                            <TextField
                              label="Landmark"
                              value={currentBranch.landmark}
                              onChange={(e) => setCurrentBranch({ ...currentBranch, landmark: e.target.value })}
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  "&:hover fieldset": { borderColor: "#FF6C6C" },
                                  "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                              }}
                            />

                            <TextField
                              label="GPS Address"
                              value={currentBranch.gpsAddress}
                              onChange={(e) => setCurrentBranch({ ...currentBranch, gpsAddress: e.target.value })}
                              variant="outlined"
                              fullWidth
                              placeholder="e.g., GA-123-4567"
                              size="small"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "8px",
                                  "&:hover fieldset": { borderColor: "#FF6C6C" },
                                  "&.Mui-focused fieldset": { borderColor: "#FF6C6C" },
                                },
                                "& .MuiInputLabel-root.Mui-focused": { color: "#FF6C6C" },
                              }}
                            />
                          </div>

                          <div className="flex gap-2 pt-1">
                            <Button
                              onClick={addBranch}
                              variant="contained"
                              fullWidth
                              size="small"
                              sx={{
                                background: "linear-gradient(135deg, #FF6C6C 0%, #FF8F8F 100%)",
                                borderRadius: "8px",
                                py: 0.8,
                                fontWeight: 600,
                                fontSize: "0.8rem",
                                "&:hover": {
                                  background: "linear-gradient(135deg, #FF8F8F 0%, #FFB3B3 100%)",
                                },
                              }}
                            >
                              {isEditing ? "Update" : "Add Branch"}
                            </Button>
                            {isEditing && (
                              <Button
                                onClick={cancelEdit}
                                variant="outlined"
                                size="small"
                                sx={{
                                  borderColor: "#FF6C6C",
                                  color: "#FF6C6C",
                                  borderRadius: "8px",
                                  py: 0.8,
                                  fontWeight: 600,
                                  minWidth: "70px",
                                  fontSize: "0.8rem",
                                  "&:hover": {
                                    borderColor: "#FF8F8F",
                                    backgroundColor: "#FF6C6C/10",
                                  },
                                }}
                              >
                                Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Branches List */}
                    <div className="space-y-2">
                      <h4 className="flex gap-1.5 items-center text-sm font-semibold text-gray-900">
                        <Building2 className="w-3.5 h-3.5 text-[#FF6C6C]" />
                        Branches ({partnerData.branches.length})
                      </h4>
                      
                      {partnerData.branches.length === 0 ? (
                        <div className="p-4 text-center bg-gray-50 rounded-lg border-2 border-gray-200 border-dashed">
                          <Store className="mx-auto mb-1.5 w-6 h-6 text-gray-400" />
                          <p className="text-xs text-gray-500">No branches added yet</p>
                          <p className="text-xs text-gray-400">Add your first branch to get started</p>
                        </div>
                      ) : (
                        <div className="overflow-y-auto space-y-1.5 max-h-72">
                          <AnimatePresence>
                            {partnerData.branches.map((branch, index) => (
                              <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                className="p-2.5 bg-white rounded-lg border border-gray-200 hover:border-[#FF6C6C]/30 hover:shadow-sm transition-all duration-200"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h5 className="mb-1 text-xs font-semibold text-gray-900">{branch.name}</h5>
                                    <div className="space-y-0.5 text-xs text-gray-600">
                                      <div className="flex gap-1 items-center">
                                        <MapPin className="w-2.5 h-2.5 text-[#FF6C6C]" />
                                        <span>{branch.location}, {branch.region}</span>
                                      </div>
                                      <div className="flex gap-1 items-center">
                                        <Phone className="w-2.5 h-2.5 text-[#FF6C6C]" />
                                        <span>233{branch.phoneNumber}</span>
                                      </div>
                                      {(branch.district || branch.suburb || branch.landmark) && (
                                        <div className="text-xs text-gray-500">
                                          {[branch.district, branch.suburb, branch.landmark].filter(Boolean).join(" • ")}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-1 ml-2">
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => editBranch(index)}
                                      className="p-1 text-blue-600 bg-blue-50 rounded-md transition-colors hover:bg-blue-100"
                                    >
                                      <Store className="w-2.5 h-2.5" />
                                    </motion.button>
                                    <motion.button
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => deleteBranch(index)}
                                      className="p-1 text-red-600 bg-red-50 rounded-md transition-colors hover:bg-red-100"
                                    >
                                      <Trash2 className="w-2.5 h-2.5" />
                                    </motion.button>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Review & Save */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ x: slideDirection * 300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: slideDirection * -300, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="space-y-3"
                >
                  <div className="mb-3 text-center">
                    <div className="inline-flex justify-center items-center mb-1.5 w-10 h-10 text-white bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
                      <Check className="w-5 h-5" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Review & Save</h3>
                    <p className="text-xs text-gray-600">Review your partner details before saving</p>
                  </div>

                  <div className="mx-auto max-w-3xl">
                    {/* Partner Summary */}
                    <div className="p-3 bg-gradient-to-br from-[#FF6C6C]/10 to-purple-50 rounded-lg border border-[#FF6C6C]/20 mb-3">
                      <div className="flex gap-2.5 items-center mb-2">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#FF6C6C] to-[#FF8F8F] rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-gray-900">{partnerData.name}</h4>
                          <p className="text-xs text-gray-600">{partnerData.branches.length} {partnerData.branches.length === 1 ? 'Branch' : 'Branches'}</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {Array.from(new Set(partnerData.branches.map(b => b.region))).map((region) => (
                          <Chip
                            key={region}
                            label={region}
                            size="small"
                            sx={{
                              backgroundColor: "#FF6C6C",
                              color: "white",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: "20px",
                            }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Branches List */}
                    <div className="space-y-2">
                      <h5 className="flex gap-1.5 items-center text-sm font-semibold text-gray-900">
                        <Store className="w-3.5 h-3.5 text-[#FF6C6C]" />
                        Branch Details
                      </h5>
                      
                      <div className="overflow-y-auto space-y-1.5 max-h-56">
                        {partnerData.branches.map((branch, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="p-2.5 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-sm"
                          >
                            <div className="flex gap-2.5 items-start">
                              <div className="w-7 h-7 bg-[#FF6C6C]/10 rounded-lg flex items-center justify-center shrink-0">
                                <Store className="w-3.5 h-3.5 text-[#FF6C6C]" />
                              </div>
                              <div className="flex-1">
                                <h6 className="mb-1 text-xs font-semibold text-gray-900">{branch.name}</h6>
                                <div className="grid grid-cols-1 gap-0.5 text-xs text-gray-600 md:grid-cols-2">
                                  <div className="flex gap-1 items-center">
                                    <MapPin className="w-2.5 h-2.5 text-[#FF6C6C]" />
                                    <span>{branch.location}, {branch.region}</span>
                                  </div>
                                  <div className="flex gap-1 items-center">
                                    <Phone className="w-2.5 h-2.5 text-[#FF6C6C]" />
                                    <span>233{branch.phoneNumber}</span>
                                  </div>
                                  {(branch.district || branch.suburb || branch.landmark || branch.gpsAddress) && (
                                    <div className="text-xs text-gray-500 md:col-span-2">
                                      {[
                                        branch.district && `District: ${branch.district}`,
                                        branch.suburb && `Suburb: ${branch.suburb}`,
                                        branch.landmark && `Landmark: ${branch.landmark}`,
                                        branch.gpsAddress && `GPS: ${branch.gpsAddress}`
                                      ].filter(Boolean).join(" • ")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer - Ultra Compressed */}
          <div className="p-2.5 bg-gray-50 border-t border-gray-200 shrink-0">
            <div className="flex justify-between items-center">
              <div className="flex gap-1.5 items-center text-xs text-gray-500">
                <Sparkles className="w-3 h-3" />
                <span>Step {currentStep} of 3</span>
              </div>

              <div className="flex gap-2">
                {currentStep > 1 && (
                  <Button
                    onClick={prevStep}
                    variant="outlined"
                    startIcon={<ArrowLeft className="w-3.5 h-3.5" />}
                    size="small"
                    sx={{
                      borderColor: "#FF6C6C",
                      color: "#FF6C6C",
                      borderRadius: "8px",
                      px: 2,
                      py: 0.8,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      "&:hover": {
                        borderColor: "#FF8F8F",
                        backgroundColor: "#FF6C6C/10",
                      },
                    }}
                  >
                    Previous
                  </Button>
                )}

                {currentStep < 3 ? (
                  <Button
                    onClick={nextStep}
                    variant="contained"
                    endIcon={<ArrowRight className="w-3.5 h-3.5" />}
                    size="small"
                    sx={{
                      background: "linear-gradient(135deg, #FF6C6C 0%, #FF8F8F 100%)",
                      borderRadius: "8px",
                      px: 2.5,
                      py: 0.8,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      "&:hover": {
                        background: "linear-gradient(135deg, #FF8F8F 0%, #FFB3B3 100%)",
                      },
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={14} color="inherit" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )
                    }
                    size="small"
                    sx={{
                      background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                      borderRadius: "8px",
                      px: 2.5,
                      py: 0.8,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      "&:hover": {
                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                      },
                      "&:disabled": {
                        background: "#9CA3AF",
                      },
                    }}
                  >
                    {isSubmitting ? "Saving..." : "Save Partner"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InlinePartnerModal; 